import clsx from 'clsx';
import React from 'react';
import { Skeleton } from './Feedback';
import { StatusLabel, type Tone } from './Status';

/**
 * AllowanceMeter: a quantity against an allowance (minutes used of minutes
 * included). Unlike the in-row `Meter`, colour here is *status*, so it is
 * semantic and always beside a word:
 *   - under `warnAt` (default 80%) → accent fill, "42% used"
 *   - at or over `warnAt`          → warning fill, "82% used · 88 min left"
 *   - over the allowance           → the track is the total used; the
 *                                    included part stays accent and the
 *                                    overflow is danger, "41 min over …"
 *   - no allowance (`max` 0)       → neutral hollow, "No included minutes"
 */
export const AllowanceMeter: React.FC<{
  label: string;
  value: number;
  max: number;
  /** Formats a quantity ("412 min"). */
  format: (n: number) => string;
  /** Fraction of `max` at which the meter turns to warning. Default 0.8. */
  warnAt?: number;
  /** One quiet line opposite the status word (e.g. the overage rate). */
  hint?: React.ReactNode;
  className?: string;
}> = ({ label, value, max, format, warnAt = 0.8, hint, className }) => {
  const over = max > 0 && value > max;
  const ratio = max > 0 ? value / max : 0;
  const pct = Math.min(100, Math.max(0, ratio * 100));

  let tone: Tone = 'accent';
  let hollow = false;
  let word: string;
  if (max <= 0) {
    tone = 'neutral';
    hollow = true;
    word = 'No included minutes';
  } else if (over) {
    tone = 'danger';
    word = `${format(value - max)} over the included ${format(max)}`;
  } else if (ratio >= warnAt) {
    tone = 'warning';
    word = `${Math.round(pct)}% used · ${format(max - value)} left`;
  } else {
    word = `${Math.round(pct)}% used`;
  }

  return (
    <div className={className}>
      <div className="flex items-baseline justify-between gap-4">
        <span className="text-v2-12 font-medium text-v2-fg-3">{label}</span>
        <span className="v2-num text-v2-13 text-v2-fg">
          <span className="font-semibold">{format(value)}</span>
          {max > 0 && <span className="text-v2-fg-3"> of {format(max)}</span>}
        </span>
      </div>
      <div
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={Math.max(max, value)}
        aria-valuenow={value}
        aria-valuetext={`${format(value)}${max > 0 ? ` of ${format(max)}` : ''}`}
        className="mt-1.5 flex h-1.5 w-full overflow-hidden rounded-full bg-v2-line"
      >
        {over ? (
          <>
            <span
              className="block h-full bg-v2-accent"
              style={{ width: `${(max / value) * 100}%` }}
            />
            <span
              className="block h-full bg-v2-danger"
              style={{ width: `${((value - max) / value) * 100}%` }}
            />
          </>
        ) : (
          <span
            className={clsx(
              'block h-full rounded-full',
              tone === 'warning' ? 'bg-v2-warning' : 'bg-v2-accent',
            )}
            style={{ width: `${Math.max(pct > 0 ? 1 : 0, pct)}%` }}
          />
        )}
      </div>
      <div className="mt-1.5 flex items-baseline justify-between gap-4">
        <StatusLabel size="sm" tone={tone} hollow={hollow}>
          {word}
        </StatusLabel>
        {hint && (
          <span className="truncate text-v2-12 text-v2-fg-3">{hint}</span>
        )}
      </div>
    </div>
  );
};

export const AllowanceMeterSkeleton: React.FC<{ className?: string }> = ({
  className,
}) => (
  <div className={className} aria-busy>
    <div className="flex items-baseline justify-between">
      <Skeleton width={88} className="h-2.5" />
      <Skeleton width={120} className="h-3" />
    </div>
    <Skeleton className="mt-2 block h-1.5 w-full" />
    <Skeleton width={72} className="mt-2 h-2.5" />
  </div>
);
